import { billingConfig } from './config';
import {
  PriceInterval,
  PriceModel,
  PriceType,
  type Plan,
  type Price
} from './schema';

export function formatCurrency(cost: number, currency?: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(cost);
}

export function formatPrice(price: Price, displayInterval: string): string {
  if (price.meter) {
    return 'Price Varies';
  }

  const cost =
    price.type === PriceType.Recurring && displayInterval === PriceInterval.Year
      ? Number(price.cost / 12)
      : price.cost;

  return formatCurrency(cost, price.currency.toLowerCase());
}

export function getProductPricePair(priceId: string) {
  for (const product of billingConfig.products) {
    for (const plan of product.plans) {
      for (const price of plan.prices)
        if (price.id === priceId) {
          return { product, price };
        }
    }
  }

  throw new Error('Price not found');
}

export function getPriceTypeById(priceId: string) {
  for (const product of billingConfig.products) {
    for (const plan of product.plans) {
      for (const price of plan.prices)
        if (price.id === priceId) {
          return price.type;
        }
    }
  }

  throw new Error(`Price with ID ${priceId} not found`);
}

export function getPriceModelById(priceId: string) {
  for (const product of billingConfig.products) {
    for (const plan of product.plans) {
      for (const price of plan.prices)
        if (price.id === priceId) {
          return price.model;
        }
    }
  }

  throw new Error(`Price with ID ${priceId} not found`);
}

/**
 * User-facing plan name for a SubscriptionTier value. Product ids in
 * billingConfig equal the tier enum values ('plus' → "Creator", 'pro' →
 * "Agency", ...), so the config is the single source for display names.
 */
export function getTierDisplayName(tier?: string | null): string {
  if (!tier) return '';
  const product = billingConfig.products.find(
    (p) => p.id === tier.toLowerCase()
  );
  return (
    product?.name ?? tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase()
  );
}

export function getPrimaryPrice(plan: Plan) {
  return (
    plan.prices.find((price) => price.model === PriceModel.Flat) ||
    plan.prices.find((price) => price.model === PriceModel.PerSeat) ||
    plan.prices[0]
  );
}

/**
 * Largest annual discount across visible subscription tiers, as a whole percent
 * (annual vs. paying the sibling monthly price — original list price when a
 * launch discount is shown — for a year). 0 if none save.
 * Drives the "Save up to X%" pill on the interval toggle.
 */
export function getMaxYearlySavingsPercent(): number {
  let max = 0;
  for (const product of billingConfig.products) {
    if (product.hidden) continue;
    const monthPlan = product.plans.find((p) =>
      p.displayIntervals.includes(PriceInterval.Month)
    );
    const yearPlan = product.plans.find((p) =>
      p.displayIntervals.includes(PriceInterval.Year)
    );
    if (!monthPlan || !yearPlan) continue;
    const monthPrice = getPrimaryPrice(monthPlan);
    const monthCost = monthPrice?.originalCost ?? monthPrice?.cost ?? 0;
    const yearCost = getPrimaryPrice(yearPlan)?.cost ?? 0;
    if (monthCost <= 0 || yearCost <= 0) continue;
    const percent = Math.round((1 - yearCost / (monthCost * 12)) * 100);
    if (percent > max) max = percent;
  }
  return max;
}

export function createPurchasesHelper(organization: {
  subscriptions: {
    active: boolean;
    items: {
      variantId: string;
    }[];
  }[];
  orders: {
    items: {
      variantId: string;
    }[];
  }[];
}) {
  const hasPurchasedProduct = (productId?: string | string[]) => {
    const productIds = productId
      ? Array.isArray(productId)
        ? productId
        : [productId]
      : billingConfig.products.map((product) => product.id);

    const priceIds = billingConfig.products
      .filter((product) => productIds.includes(product.id))
      .flatMap((product) =>
        product.plans.flatMap((plan) => plan.prices.map((p) => p.id))
      );

    const matchSubscription = organization.subscriptions
      .filter((sub) => sub.active)
      .flatMap((sub) => sub.items)
      .some((item) => priceIds.includes(item.variantId));

    const matchOrder = organization.orders
      .flatMap((order) => order.items)
      .some((item) => priceIds.includes(item.variantId));

    return matchSubscription || matchOrder;
  };

  const purchasedProducts = billingConfig.products.filter((product) =>
    hasPurchasedProduct(product.id)
  );

  return {
    purchasedProducts,
    hasPurchasedProduct
  };
}
